import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, HelpCircle, FileText, ArrowRight, ShieldCheck, Clock } from "lucide-react";

const Stitch = ({ className = "", color = "#E0A526" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const fieldBase =
  "mbf-body w-full bg-transparent border-b-2 border-[#0A355C]/15 focus:border-[#2E7D32] outline-none py-2.5 text-[#0A355C] placeholder:text-[#0A355C]/35 transition-colors duration-300";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "" });
  const [sent, setSent] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <div className="bg-[#FBF9F3] min-h-screen pt-[84px] pb-16">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* Hero Header */}
      <section className="bg-[#082440] text-white py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 34px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 34px)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <span className="mbf-mono text-[12px] tracking-[0.2em] uppercase text-[#E0A526]">
            Get In Touch
          </span>
          <h1 className="mbf-display text-4xl sm:text-5xl font-semibold mt-4 tracking-tight">
            Connect With the Trust
          </h1>
          <Stitch className="w-32 h-2 mx-auto mt-6" color="#E0A526" />
          <p className="mbf-body mt-6 text-blue-100/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Write to us regarding student enrollments, volunteering paths, CSR partnerships, or compliance information.
          </p>
        </div>
      </section>

      {/* Main Form & details block */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
          
          {/* Left Column: Form */}
          <div className="lg:col-span-7 bg-white border border-[#0A355C]/10 p-8 md:p-10 flex flex-col justify-between">
            {sent ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20">
                <div className="w-14 h-14 rounded-full border-2 border-[#2E7D32] flex items-center justify-center mb-5 bg-[#F2ECDD]/20">
                  <Send className="w-6 h-6 text-[#2E7D32]" />
                </div>
                <h3 className="mbf-display text-2xl font-semibold text-[#0A355C]">
                  Message Sent Successfully
                </h3>
                <p className="mbf-body text-[14px] text-[#514a3d] mt-3 max-w-sm">
                  Thank you for reaching out to MB Foundation. A coordinator will process your request and write back shortly.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-8 mbf-mono text-xs text-[#2E7D32] font-semibold uppercase tracking-wider hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="mbf-mono text-[11px] uppercase tracking-wide text-[#0A355C]/60 block mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      className={fieldBase}
                    />
                  </div>
                  <div>
                    <label className="mbf-mono text-[11px] uppercase tracking-wide text-[#0A355C]/60 block mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className={fieldBase}
                    />
                  </div>
                </div>

                <div>
                  <label className="mbf-mono text-[11px] uppercase tracking-wide text-[#0A355C]/60 block mb-1">
                    Subject / Program Interest
                  </label>
                  <select
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    className="mbf-body w-full bg-transparent border-b-2 border-[#0A355C]/15 focus:border-[#2E7D32] outline-none py-2.5 text-[#0A355C] transition-colors duration-300"
                  >
                    <option value="general">General Inquiry / Feedback</option>
                    <option value="tailoring">Tailoring &amp; Design Course</option>
                    <option value="computers">Computer &amp; IT Literacy</option>
                    <option value="farming">Organic Agriculture Training</option>
                    <option value="csr">CSR Sponsorship &amp; Partnerships</option>
                    <option value="volunteer">Volunteering Opportunities</option>
                  </select>
                </div>

                <div>
                  <label className="mbf-mono text-[11px] uppercase tracking-wide text-[#0A355C]/60 block mb-1">
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us what you're looking for..."
                    className={`${fieldBase} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  className="relative inline-flex items-center gap-2 pl-6 pr-5 py-3.5 text-[#0A355C] font-semibold transition-transform duration-200 hover:-translate-y-0.5"
                  style={{
                    background: "#E0A526",
                    clipPath: "polygon(14px 0, 100% 0, 100% 100%, 14px 100%, 0 50%)",
                  }}
                >
                  <span className="absolute left-[7px] top-1/2 -translate-y-1/2 w-[6px] h-[6px] rounded-full bg-[#0A355C]/70 ring-2 ring-[#E0A526]" />
                  Send Message
                </button>
              </form>
            )}
          </div>

          {/* Right Column: Office info tag */}
          <div className="lg:col-span-5 flex flex-col justify-between bg-[#0A355C] text-white p-8 md:p-10 relative">
            {/* background grid */}
            <div
              className="absolute inset-0 opacity-[0.02] pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 30px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 30px)",
              }}
            />
            
            <div className="relative z-10">
              <span className="mbf-mono text-[11px] uppercase tracking-[0.18em] text-[#E0A526]">
                Registered Office
              </span>
              <Stitch className="w-16 h-2 mt-4" color="#E0A526" />

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0 bg-white/5">
                    <MapPin className="w-4 h-4 text-[#E0A526]" />
                  </div>
                  <div>
                    <h4 className="mbf-mono text-[11px] uppercase tracking-wide text-blue-100/50">Address</h4>
                    <p className="mbf-body text-[14.5px] text-blue-100/80 leading-relaxed mt-1">
                      No. 2/116 C, First Street,<br />
                      Samy Nagar, Bagayam,<br />
                      Vellore – 632002, Tamil Nadu
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0 bg-white/5">
                    <Phone className="w-4 h-4 text-[#E0A526]" />
                  </div>
                  <div>
                    <h4 className="mbf-mono text-[11px] uppercase tracking-wide text-blue-100/50">Telephone</h4>
                    <a href="tel:+917208058502" className="mbf-body text-[14.5px] text-blue-100/80 hover:text-white transition-colors mt-1 block">
                      +91 72080 58502
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0 bg-white/5">
                    <Mail className="w-4 h-4 text-[#E0A526]" />
                  </div>
                  <div>
                    <h4 className="mbf-mono text-[11px] uppercase tracking-wide text-blue-100/50">Email Support</h4>
                    <a href="mailto:contact@mbfoundationsdl.org" className="mbf-body text-[14.5px] text-blue-100/80 hover:text-white transition-colors mt-1 block">
                      contact@mbfoundationsdl.org
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0 bg-white/5">
                    <Clock className="w-4 h-4 text-[#E0A526]" />
                  </div>
                  <div>
                    <h4 className="mbf-mono text-[11px] uppercase tracking-wide text-blue-100/50">Office Hours</h4>
                    <p className="mbf-body text-[14.5px] text-blue-100/80 leading-relaxed mt-1">
                      Mon - Sat: 9:00 AM - 5:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 mt-10 pt-6 relative z-10">
              <p className="mbf-mono text-[10px] text-blue-100/40 tracking-wider">
                REG. TRUST NUMBER: EST. 18.09.2015
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Helpful Links Grid */}
      <section className="bg-[#F2ECDD] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 border border-[#0A355C]/10 flex gap-4">
              <div className="w-10 h-10 rounded-full bg-[#0A355C]/5 flex items-center justify-center text-[#2E7D32] shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h4 className="mbf-display font-semibold text-[#0A355C] text-base">CSR compliance</h4>
                <p className="mbf-body text-xs text-[#6b6355] mt-1.5 leading-relaxed">
                  We supply 80G tax exemptions, project auditing logs, and structured audit paperwork for voluntary compliant partnership checks.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-6 border border-[#0A355C]/10 flex gap-4">
              <div className="w-10 h-10 rounded-full bg-[#0A355C]/5 flex items-center justify-center text-[#2E7D32] shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h4 className="mbf-display font-semibold text-[#0A355C] text-base">Trainee admission FAQ</h4>
                <p className="mbf-body text-xs text-[#6b6355] mt-1.5 leading-relaxed">
                  Learn about training center facilities, student resources, transport services, and attendance requirements in our catalog pages.
                </p>
              </div>
            </div>

            <div className="bg-white p-6 border border-[#0A355C]/10 flex gap-4">
              <div className="w-10 h-10 rounded-full bg-[#0A355C]/5 flex items-center justify-center text-[#2E7D32] shrink-0">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="mbf-display font-semibold text-[#0A355C] text-base">Volunteering guide</h4>
                <p className="mbf-body text-xs text-[#6b6355] mt-1.5 leading-relaxed">
                  Interested in teaching computer skills, tailoring, or hosting farming clinics? Reach out to schedule a visit to our center.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
